import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

// The keys used to cache the truncated text values of rows so it doesn't have to be recalculated.
export enum TruncatedTextCacheKeys {
	PAGE = 'truncatedPage',
	SPACE = 'truncatedSpace',
	ASSIGNEE = 'truncatedAssignee',
	CREATED_BY = 'truncatedCreatedBy',
}

// Type to store information regarding the rendering of a row.
export interface TaskRow {
	taskId: string;

	// Controls the height of the task row in task table. Set by the description height in pixels.
	// You cannot assign a row height but we can set the container heights to match the description.
	// Set inside the DescriptionColumn component.
	rowHeight: number;

	// A flag to see if calculations are currently happening.
	isLoading: boolean;

	// Used to store the calculated truncated page text
	[TruncatedTextCacheKeys.PAGE]?: string;
	[TruncatedTextCacheKeys.SPACE]?: string;
	[TruncatedTextCacheKeys.ASSIGNEE]?: string;
	[TruncatedTextCacheKeys.CREATED_BY]?: string;
}

type TaskRows = Record<string, TaskRow>;

export interface TaskRowsContextType {
	taskRows: TaskRows;
	setTaskRow: (id: string, row: Partial<TaskRow>) => void; // Sets the row and overrides the values passed.
	isLoading: boolean;
}

export const TaskRowsContext = createContext<TaskRowsContextType>({
	taskRows: {},
	setTaskRow: () => null,
	isLoading: false,
});

export const useTaskRowsContext = () => useContext(TaskRowsContext);

export const TaskRowsProvider = ({ children }) => {
	const [taskRows, setRowsTasksState] = useState<TaskRows>({});

	const isLoading = useMemo(
		() => Object.values(taskRows).some((taskRow) => taskRow.isLoading),
		[taskRows],
	);

	const setTaskRow = useCallback((taskId: string, task: Partial<TaskRow>) => {
		setRowsTasksState((prev) => ({
			...prev,
			[taskId]: {
				...prev[taskId],
				...task,
			},
		}));
	}, []);

	const taskRowsContextValue = useMemo(
		() => ({ taskRows, setTaskRow, isLoading }),
		[taskRows, setTaskRow, isLoading],
	);

	return (
		<TaskRowsContext.Provider value={taskRowsContextValue}>{children}</TaskRowsContext.Provider>
	);
};
